<template>
  <div>
    <CRow class="mb-3 align-items-center">
      <CCol sm="12">
        <h4 class="mb-0 text-primary"><i class="fas fa-chart-pie"></i> Dashboard de Rentabilidad y Utilidad</h4>
        <p class="text-muted small mb-0">Análisis financiero de fletes, ventas y gastos operativos.</p>
      </CCol>
    </CRow>

    <!-- FILTROS DE FECHA ÚNICOS -->
    <CRow class="mb-4">
      <CCol lg="3" md="6" class="mb-2">
        <label class="small font-weight-bold text-muted">Fecha Desde *</label>
        <input type="date" class="form-control form-control-sm" v-model="filters.date_from" @change="applyAllFilters">
      </CCol>
      <CCol lg="3" md="6" class="mb-2">
        <label class="small font-weight-bold text-muted">Fecha Hasta *</label>
        <input type="date" class="form-control form-control-sm" v-model="filters.date_to" @change="applyAllFilters">
      </CCol>
      <CCol lg="2" md="6" class="mb-2 d-flex align-items-end">
        <button class="btn btn-sm btn-info font-weight-bold" @click="applyAllFilters">
          <i class="fas fa-search"></i> Aplicar Filtros
        </button>
      </CCol>
      <CCol lg="4" md="6" class="mb-2 d-flex align-items-end justify-content-end">
        <button class="btn btn-sm btn-outline-secondary font-weight-bold mr-2" @click="forceRefresh">
          <i class="fas fa-sync"></i> Refrescar
        </button>
        <div class="text-muted small">
          <i class="fas fa-info-circle"></i> Todos los reportes se actualizan con el rango.
        </div>
      </CCol>
    </CRow>

    <!-- ALERTA SI HAY ERRORES -->
    <CAlert v-if="globalError" color="danger" class="mb-3">
      <i class="fas fa-exclamation-triangle"></i>
      <strong>Error al cargar datos:</strong> {{ globalError }}
      <button class="btn btn-sm btn-light ml-2" @click="forceRefresh">Reintentar</button>
    </CAlert>

    <!-- ============================================ -->
    <!-- ✅ KPIs: Gastos Vendedor / Gastos Chofer / Ventas Totales / Utilidad Total -->
    <!-- La 4ta card "Utilidad Total" = subtotal del desglose por viaje -->
    <!-- ============================================ -->
    <CRow class="mb-4 text-center">
      <CCol md="3" sm="6" class="mb-3">
        <CCard class="shadow-sm border-danger h-100">
          <CCardBody class="p-3 d-flex flex-column justify-content-center">
            <div class="text-muted small text-uppercase font-weight-bold mb-1">
              <i class="fas fa-receipt text-danger"></i> Gastos Vendedor
            </div>
            <h4 class="text-danger mb-0 font-weight-bold">S/ {{ safeNumber(totals.gastos_vendedor) }}</h4>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol md="3" sm="6" class="mb-3">
        <CCard class="shadow-sm border-warning h-100">
          <CCardBody class="p-3 d-flex flex-column justify-content-center">
            <div class="text-muted small text-uppercase font-weight-bold mb-1">
              <i class="fas fa-tools text-warning"></i> Gastos Chofer
            </div>
            <h4 class="text-warning mb-0 font-weight-bold">S/ {{ safeNumber(totals.gastos_chofer) }}</h4>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol md="3" sm="6" class="mb-3">
        <CCard class="shadow-sm border-info h-100">
          <CCardBody class="p-3 d-flex flex-column justify-content-center">
            <div class="text-muted small text-uppercase font-weight-bold mb-1">
              <i class="fas fa-coins text-info"></i> Ventas Totales
            </div>
            <h4 class="text-info mb-0 font-weight-bold">S/ {{ safeNumber(totals.ventas) }}</h4>
          </CCardBody>
        </CCard>
      </CCol>
      <!-- ✅ 4ta card: UTILIDAD TOTAL (igual al subtotal del desglose por viaje) -->
      <CCol md="3" sm="6" class="mb-3">
        <CCard class="shadow-sm h-100" :class="parseFloat(totals.utilidad_neta) >= 0 ? 'bg-primary text-white' : 'bg-warning text-dark'">
          <CCardBody class="p-3 d-flex flex-column justify-content-center">
            <div class="small text-uppercase font-weight-bold mb-1"><i class="fas fa-chart-line"></i> Utilidad Total</div>
            <h3 class="mb-0 font-weight-bold">S/ {{ safeNumber(totals.utilidad_neta) }}</h3>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- 2. GRÁFICOS PRINCIPALES -->
    <CRow>
      <CCol lg="8" class="mb-4">
        <CCard class="shadow-sm border-0 h-100">
          <CCardHeader class="bg-dark text-white font-weight-bold">
            <i class="fas fa-chart-bar"></i> Rentabilidad Mensual
          </CCardHeader>
          <CCardBody>
            <div v-if="isLoading" class="text-center py-5 text-muted">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
              <p class="mt-2 mb-0 small">Cargando...</p>
            </div>
            <div v-else-if="monthlyData.length === 0" class="text-center py-5 text-muted font-weight-bold">
              No hay datos mensuales en el rango seleccionado.
            </div>
            <CChartBar
              v-else
              :datasets="barChartData"
              :options="barChartOptions"
              :labels="monthlyLabels"
              style="height: 300px;"
            />
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="4" class="mb-4">
        <CCard class="shadow-sm border-0 h-100">
          <CCardHeader class="bg-dark text-white font-weight-bold">
            <i class="fas fa-chart-pie"></i> Distribución Ingresos vs Gastos
          </CCardHeader>
          <CCardBody class="d-flex align-items-center justify-content-center">
            <div v-if="isLoading" class="text-center py-5 text-muted">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
            </div>
            <div v-else-if="parseFloat(totals.ventas) === 0 && parseFloat(totals.gastos_vendedor) === 0 && parseFloat(totals.gastos_chofer) === 0" class="text-muted font-weight-bold text-center py-5">
              No hay movimientos financieros en el rango seleccionado.
            </div>
            <CChartPie
              v-else
              :datasets="pieChartData"
              :labels="['Ventas', 'Gastos Vendedor', 'Gastos Chofer']"
              :options="{ responsive: true, maintainAspectRatio: false }"
              style="height: 250px; width: 100%;"
            />
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- 3. REPORTES AVANZADOS -->
    <CRow class="mb-4">
      <CCol lg="6" class="mb-4">
        <CCard class="shadow-sm border-0 h-100">
          <CCardHeader class="bg-success text-white font-weight-bold">
            <i class="fas fa-chart-line"></i> Ventas Mensuales (Evolución)
          </CCardHeader>
          <CCardBody>
            <div v-if="loadingSales" class="text-center py-5 text-muted">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
            </div>
            <div v-else-if="monthlySalesData.length === 0" class="text-muted font-weight-bold text-center py-5">
              No hay datos de ventas en el rango seleccionado.
            </div>
            <div v-else>
              <CChartBar
                :datasets="monthlySalesChartData"
                :labels="monthlySalesLabels"
                :options="monthlyChartOptions"
                style="height: 250px;"
              />
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="6" class="mb-4">
        <CCard class="shadow-sm border-0 h-100">
          <CCardHeader class="bg-warning text-dark font-weight-bold">
            <i class="fas fa-chart-line"></i> Compras Mensuales (Merma)
          </CCardHeader>
          <CCardBody>
            <div v-if="loadingPurchases" class="text-center py-5 text-muted">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
            </div>
            <div v-else-if="monthlyPurchasesData.length === 0" class="text-muted font-weight-bold text-center py-5">
              No hay datos de compras en el rango seleccionado.
            </div>
            <div v-else>
              <CChartBar
                :datasets="monthlyPurchasesChartData"
                :labels="monthlyPurchasesLabels"
                :options="monthlyChartOptions"
                style="height: 250px;"
              />
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- 4. RANKING DE CLIENTES Y TOP PRODUCTOS -->
    <CRow class="mb-4">
      <CCol md="6" class="mb-4">
        <CCard class="shadow-sm border-0 h-100">
          <CCardHeader class="bg-primary text-white font-weight-bold">
            <i class="fas fa-trophy"></i> Top 10 Clientes Potenciales
          </CCardHeader>
          <CCardBody>
            <div v-if="loadingRanking" class="text-center py-5 text-muted">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
            </div>
            <div v-else-if="rankingClients.length === 0" class="text-muted font-weight-bold text-center py-5">
              No hay datos de clientes en el rango seleccionado.
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-striped text-center align-middle">
                <thead class="bg-dark text-white">
                  <tr>
                    <th>#</th>
                    <th class="text-left">Cliente</th>
                    <th>Documento</th>
                    <th>Compras</th>
                    <th>Pies</th>
                    <th>Gastado (S/)</th>
                    <th>Última Compra</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(client, index) in rankingClients" :key="client.client_id || index">
                    <td class="font-weight-bold">{{ index + 1 }}</td>
                    <td class="text-left font-weight-bold">{{ client.client_name }}</td>
                    <td>{{ client.client_document }}</td>
                    <td class="text-primary font-weight-bold">{{ client.total_purchases }}</td>
                    <td class="text-info font-weight-bold">{{ client.total_pies }}</td>
                    <td class="text-success font-weight-bold">S/ {{ safeNumber(client.total_spent) }}</td>
                    <td><small>{{ formatShortDate(client.last_purchase) }}</small></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol md="6" class="mb-4">
        <CCard class="shadow-sm border-0 h-100">
          <CCardHeader class="bg-info text-white font-weight-bold">
            <i class="fas fa-tree"></i> Tipos de Madera Más Vendidos
          </CCardHeader>
          <CCardBody>
            <div v-if="loadingTopProducts" class="text-center py-5 text-muted">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
            </div>
            <div v-else-if="topProducts.length === 0" class="text-muted font-weight-bold text-center py-5">
              No hay datos de productos en el rango seleccionado.
            </div>
            <div v-else>
              <CChartBar
                :datasets="topProductsChartData"
                :labels="topProductsLabels"
                :options="topProductsChartOptions"
                style="height: 300px;"
              />
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- ============================================ -->
    <!-- ✅ DESGLOSE DE UTILIDAD POR VIAJE          -->
    <!-- Solo Utilidad (sin Gastos ni Combustible)  -->
    <!-- El subtotal = utilidad_neta del backend    -->
    <!-- ============================================ -->
    <CRow class="mb-4">
      <CCol lg="12" class="mb-4">
        <CCard class="shadow-sm border-0">
          <CCardHeader class="bg-dark text-white font-weight-bold d-flex justify-content-between align-items-center">
            <span>
              <i class="fas fa-calculator"></i> Desglose de Utilidad por Viaje
            </span>
            <span class="badge badge-light text-dark">
              {{ tripsBreakdown.length }} viajes
            </span>
          </CCardHeader>
          <CCardBody>
            <div class="table-responsive">
              <table class="table table-hover table-striped text-center align-middle mb-0">
                <thead class="bg-dark text-white">
                  <tr>
                    <th>#</th>
                    <th>Fecha</th>
                    <th>Vehículo</th>
                    <th>Chofer</th>
                    <th class="text-right">Utilidad (S/)</th>
                    <th>Auditar</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(trip, index) in tripsBreakdown" :key="trip.trip_id">
                    <td>{{ index + 1 }}</td>
                    <td>{{ formatShortDate(trip.fecha_inicio) }}</td>
                    <td>
                      <span class="badge badge-secondary">{{ trip.vehicle_plate }}</span>
                    </td>
                    <td>
                      <small>{{ trip.driver_name }}</small>
                    </td>
                    <!-- ✅ Utilidad real del backend (fórmula detalle por tipo de madera) -->
                    <td class="text-right font-weight-bold"
                        :class="parseFloat(trip.utilidad) >= 0 ? 'text-success' : 'text-danger'">
                      S/ {{ safeNumber(trip.utilidad) }}
                    </td>
                    <td>
                      <button
                        class="btn btn-sm btn-dark"
                        @click="viewTripHistory(trip.trip_id)"
                        title="Ver Detalle del Viaje"
                      >
                        <i class="fas fa-eye"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="tripsBreakdown.length === 0">
                    <td colspan="6" class="text-muted py-4">
                      No hay viajes registrados en el período seleccionado.
                    </td>
                  </tr>
                </tbody>
                <tfoot v-if="tripsBreakdown.length > 0" class="bg-light font-weight-bold">
                  <tr>
                    <td colspan="4" class="text-right text-dark">
                      SUBTOTALES:
                    </td>
                    <!-- ✅ Subtotal = suma de trip.utilidad (debe coincidir con la 4ta card) -->
                    <td class="text-right"
                        :class="parseFloat(tripsTotales.utilidad) >= 0 ? 'text-success' : 'text-danger'">
                      S/ {{ safeNumber(tripsTotales.utilidad) }}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- ================================================================ -->
    <!-- MODAL DE AUDITORÍA DE RUTA (SIN CAMBIOS)                        -->
    <!-- ================================================================ -->
    <CModal :show.sync="showHistoryModal" title="Auditoría de Ruta" color="dark" size="xl">
      <div v-if="isLoadingHistory" class="text-center p-5">
        <i class="fas fa-spinner fa-spin fa-3x text-primary mb-3"></i>
        <p>Cargando información detallada del viaje...</p>
      </div>
      <div v-else-if="historyTrip" class="p-2">
        <CRow class="mb-3 border-bottom pb-3">
          <CCol sm="3">
            <span class="small text-muted font-weight-bold text-uppercase">Chofer</span>
            <h5 class="text-dark mb-0">{{ historyTrip.driver_name }}</h5>
            <small>DNI: {{ historyTrip.driver_dni }}</small>
          </CCol>
          <CCol sm="3">
            <span class="small text-muted font-weight-bold text-uppercase">Vehículo / Flete</span>
            <h5 class="text-dark mb-0"><i class="fas fa-truck"></i> {{ historyTrip.vehicle_plate }}</h5>
            <small class="text-danger font-weight-bold">Flete: S/ {{ parseFloat(historyTrip.freight_cost).toFixed(2) }}</small>
            <br>
            <small class="text-info">{{ historyTrip.flete_type == 'PRIVADO' ? 'Flete Privado' : 'Flete Particular' }}</small>
          </CCol>
          <CCol sm="3">
            <span class="small text-muted font-weight-bold text-uppercase">Vendedor</span>
            <h5 class="text-dark mb-0">
              {{ historyTrip.seller_name || 'No asignado' }}
            </h5>
          </CCol>
          <CCol sm="3" class="text-right">
            <span class="small text-muted font-weight-bold text-uppercase">Cuadrilla / Estado</span>
            <h5 class="mb-0 font-weight-bold" :class="historyTrip.status == 'CERRADO' ? 'text-secondary' : 'text-primary'">
              {{ historyTrip.status }}
            </h5>
            <small v-if="historyTrip.crew">
              <i class="fas fa-users"></i> {{ historyTrip.crew.name }}
            </small>
            <small v-else class="text-muted">Sin cuadrilla</small>
          </CCol>
        </CRow>

        <!-- BILLETERAS -->
        <CRow class="mb-3">
          <CCol md="6" v-if="getChoferWallet(historyTrip)">
            <CCard class="shadow-sm border-0 h-100" style="background: #1a1a2e;">
              <CCardBody class="p-3 text-white rounded">
                <div class="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
                  <h6 class="mb-0 font-weight-bold text-uppercase">
                    <i class="fas fa-user-circle"></i> CHOFER - {{ historyTrip.driver_name }}
                  </h6>
                </div>

                <div class="mb-2">
                  <small class="text-success font-weight-bold"><i class="fas fa-arrow-down"></i> INGRESOS (Antes de Liquidación)</small>
                  <div class="table-responsive">
                    <table class="table table-sm table-bordered text-center align-middle mb-0" style="background: rgba(255,255,255,0.05);">
                      <thead>
                        <tr class="text-muted" style="font-size: 10px;">
                          <th class="text-white">Fecha</th>
                          <th class="text-white">Concepto</th>
                          <th class="text-white">Método</th>
                          <th class="text-white">Monto</th>
                          <th class="text-white">N° Op.</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style="font-size: 11px; background: rgba(40, 167, 69, 0.1);">
                          <td class="text-white">{{ formatShortDate(historyTrip.created_at) }}</td>
                          <td class="text-left text-white font-weight-bold text-success">CAJA CHICA INICIAL</td>
                          <td class="text-white">EFECTIVO</td>
                          <td class="text-success font-weight-bold">+ S/ {{ parseFloat(historyTrip.initial_cash || 0).toFixed(2) }}</td>
                          <td class="text-white">-</td>
                        </tr>
                        <tr v-for="trans in getChoferWallet(historyTrip).transactions" :key="'chofer-ingreso-'+trans.id" v-if="trans.type === 'RECARGA'" style="font-size: 11px;">
                          <td class="text-white">{{ formatShortDate(trans.transaction_date) }}</td>
                          <td class="text-left text-white">{{ trans.description || 'Recarga' }}</td>
                          <td class="text-white">{{ trans.method }}</td>
                          <td class="text-success font-weight-bold">+ S/ {{ parseFloat(trans.amount).toFixed(2) }}</td>
                          <td class="text-white">{{ trans.operation_number || '-' }}</td>
                        </tr>
                        <tr v-for="freight in historyTrip.return_freights" :key="'chofer-freight-'+freight.id" v-if="historyTrip.return_freights && historyTrip.return_freights.length > 0" style="font-size: 11px;">
                          <td class="text-white">{{ formatShortDate(freight.operation_date) }}</td>
                          <td class="text-left text-white">Flete de Retorno</td>
                          <td class="text-white">EFECTIVO</td>
                          <td class="text-success font-weight-bold">+ S/ {{ parseFloat(freight.amount).toFixed(2) }}</td>
                          <td class="text-white">-</td>
                        </tr>
                      </tbody>
                      <tfoot class="bg-success text-white" style="font-size: 11px;">
                        <tr>
                          <td colspan="3" class="text-right font-weight-bold text-white">TOTAL INGRESOS:</td>
                          <td class="font-weight-bold text-white">S/ {{ getTotalChoferIngresos(historyTrip) }}</td>
                          <td class="text-white"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                <div class="mt-2">
                  <small class="text-danger font-weight-bold"><i class="fas fa-arrow-up"></i> EGRESOS (Antes de Liquidación)</small>
                  <div class="table-responsive">
                    <table class="table table-sm table-bordered text-center align-middle mb-0" style="background: rgba(255,255,255,0.05);">
                      <thead>
                        <tr class="text-muted" style="font-size: 10px;">
                          <th class="text-white">Fecha</th>
                          <th class="text-white">Concepto</th>
                          <th class="text-white">Método</th>
                          <th class="text-white">Monto</th>
                          <th class="text-white">N° Op.</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="exp in getChoferExpenses(historyTrip)" :key="'chofer-egreso-'+exp.id" style="font-size: 11px;">
                          <td class="text-white">{{ formatShortDate(exp.operation_date) }}</td>
                          <td class="text-left text-white">{{ exp.description }}</td>
                          <td class="text-white">{{ exp.payment_method }}</td>
                          <td class="text-danger font-weight-bold">- S/ {{ parseFloat(exp.amount).toFixed(2) }}</td>
                          <td class="text-white">{{ exp.operation_number || '-' }}</td>
                        </tr>
                        <tr v-if="getChoferExpenses(historyTrip).length === 0">
                          <td colspan="5" class="text-muted py-1 text-white" style="font-size: 11px;">Sin egresos registrados</td>
                        </tr>
                      </tbody>
                      <tfoot class="bg-danger text-white" style="font-size: 11px;">
                        <tr>
                          <td colspan="3" class="text-right font-weight-bold text-white">TOTAL EGRESOS:</td>
                          <td class="font-weight-bold text-white">S/ {{ getTotalChoferEgresos(historyTrip) }}</td>
                          <td class="text-white"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                <div class="mt-2 pt-2 border-top text-center">
                  <div class="row">
                    <div class="col-6 border-right">
                      <small class="text-muted text-white">INGRESOS TOTALES</small>
                      <h5 class="mb-0 font-weight-bold text-success">S/ {{ getTotalChoferIngresos(historyTrip) }}</h5>
                    </div>
                    <div class="col-6">
                      <small class="text-muted text-white">EGRESOS TOTALES</small>
                      <h5 class="mb-0 font-weight-bold text-danger">S/ {{ getTotalChoferEgresos(historyTrip) }}</h5>
                    </div>
                  </div>
                  <div class="mt-2 pt-2 border-top">
                    <small class="text-muted text-white">SALDO NETO (Ingresos - Egresos)</small>
                    <h4 class="mb-0 font-weight-bold" :class="parseFloat(getTotalChoferNeto(historyTrip)) >= 0 ? 'text-success' : 'text-danger'">
                      S/ {{ getTotalChoferNeto(historyTrip) }}
                    </h4>
                  </div>
                </div>
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md="6" v-if="getVendedorWallet(historyTrip)">
            <CCard class="shadow-sm border-0 h-100" style="background: #1a1a2e;">
              <CCardBody class="p-3 text-white rounded">
                <div class="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
                  <h6 class="mb-0 font-weight-bold text-uppercase">
                    <i class="fas fa-user-circle"></i> VENDEDOR - {{ historyTrip.seller_name || 'No asignado' }}
                  </h6>
                </div>

                <div class="mb-2">
                  <small class="text-success font-weight-bold"><i class="fas fa-arrow-down"></i> INGRESOS (Antes de Liquidación)</small>
                  <div class="table-responsive">
                    <table class="table table-sm table-bordered text-center align-middle mb-0" style="background: rgba(255,255,255,0.05);">
                      <thead>
                        <tr class="text-muted" style="font-size: 10px;">
                          <th class="text-white">Fecha</th>
                          <th class="text-white">Concepto</th>
                          <th class="text-white">Método</th>
                          <th class="text-white">Monto</th>
                          <th class="text-white">N° Op.</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="trans in getVendedorWallet(historyTrip).transactions" :key="'vendedor-ingreso-'+trans.id" v-if="trans.type === 'RECARGA'" style="font-size: 11px;">
                          <td class="text-white">{{ formatShortDate(trans.transaction_date) }}</td>
                          <td class="text-left text-white">{{ trans.description || 'Recarga' }}</td>
                          <td class="text-white">{{ trans.method }}</td>
                          <td class="text-success font-weight-bold">+ S/ {{ parseFloat(trans.amount).toFixed(2) }}</td>
                          <td class="text-white">{{ trans.operation_number || '-' }}</td>
                        </tr>
                        <tr v-for="sale in historyTrip.sales" :key="'vendedor-venta-'+sale.id" v-if="sale.status === 'ACTIVO'" style="font-size: 11px;">
                          <td class="text-white">{{ formatShortDate(sale.operation_date) }}</td>
                          <td class="text-left text-white">Venta: {{ sale.invoice_code }}</td>
                          <td class="text-white">{{ sale.payment_method }}</td>
                          <td class="text-success font-weight-bold">+ S/ {{ parseFloat(sale.total_amount).toFixed(2) }}</td>
                          <td class="text-white">-</td>
                        </tr>
                      </tbody>
                      <tfoot class="bg-success text-white" style="font-size: 11px;">
                        <tr>
                          <td colspan="3" class="text-right font-weight-bold text-white">TOTAL INGRESOS:</td>
                          <td class="font-weight-bold text-white">S/ {{ getTotalVendedorIngresos(historyTrip) }}</td>
                          <td class="text-white"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                <div class="mt-2">
                  <small class="text-danger font-weight-bold"><i class="fas fa-arrow-up"></i> EGRESOS (Antes de Liquidación)</small>
                  <div class="table-responsive">
                    <table class="table table-sm table-bordered text-center align-middle mb-0" style="background: rgba(255,255,255,0.05);">
                      <thead>
                        <tr class="text-muted" style="font-size: 10px;">
                          <th class="text-white">Fecha</th>
                          <th class="text-white">Concepto</th>
                          <th class="text-white">Método</th>
                          <th class="text-white">Monto</th>
                          <th class="text-white">N° Op.</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="exp in getVendedorExpenses(historyTrip)" :key="'vendedor-egreso-'+exp.id" style="font-size: 11px;">
                          <td class="text-white">{{ formatShortDate(exp.operation_date) }}</td>
                          <td class="text-left text-white">{{ exp.description }}</td>
                          <td class="text-white">{{ exp.payment_method }}</td>
                          <td class="text-danger font-weight-bold">- S/ {{ parseFloat(exp.amount).toFixed(2) }}</td>
                          <td class="text-white">{{ exp.operation_number || '-' }}</td>
                        </tr>
                        <tr v-for="pur in historyTrip.purchases" :key="'vendedor-compra-'+pur.id" style="font-size: 11px;">
                          <td class="text-white">{{ formatShortDate(pur.operation_date) }}</td>
                          <td class="text-left text-white">Compra: {{ pur.type }}</td>
                          <td class="text-white">{{ pur.payment_method }}</td>
                          <td class="text-danger font-weight-bold">- S/ {{ parseFloat(pur.amount).toFixed(2) }}</td>
                          <td class="text-white">{{ pur.operation_number || '-' }}</td>
                        </tr>
                      </tbody>
                      <tfoot class="bg-danger text-white" style="font-size: 11px;">
                        <tr>
                          <td colspan="3" class="text-right font-weight-bold text-white">TOTAL EGRESOS:</td>
                          <td class="font-weight-bold text-white">S/ {{ getTotalVendedorEgresos(historyTrip) }}</td>
                          <td class="text-white"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                <div class="mt-2 pt-2 border-top text-center">
                  <div class="row">
                    <div class="col-6 border-right">
                      <small class="text-muted text-white">INGRESOS TOTALES</small>
                      <h5 class="mb-0 font-weight-bold text-success">S/ {{ getTotalVendedorIngresos(historyTrip) }}</h5>
                    </div>
                    <div class="col-6">
                      <small class="text-muted text-white">EGRESOS TOTALES</small>
                      <h5 class="mb-0 font-weight-bold text-danger">S/ {{ getTotalVendedorEgresos(historyTrip) }}</h5>
                    </div>
                  </div>
                  <div class="mt-2 pt-2 border-top">
                    <small class="text-muted text-white">SALDO NETO (Ingresos - Egresos)</small>
                    <h4 class="mb-0 font-weight-bold" :class="parseFloat(getTotalVendedorNeto(historyTrip)) >= 0 ? 'text-success' : 'text-danger'">
                      S/ {{ getTotalVendedorNeto(historyTrip) }}
                    </h4>
                  </div>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <!-- CxC -->
        <CRow>
          <CCol md="12" class="mb-4">
            <CCard class="shadow-sm border-0" style="background: #1a1a2e;">
              <CCardHeader class="bg-warning text-dark font-weight-bold">
                <i class="fas fa-file-invoice"></i> 📊 CUENTAS POR COBRAR (CxC) - FALTANTE POR COBRAR
                <span class="badge badge-dark ml-2">Total Pendiente: S/ {{ getTotalCxcPendingHistorial(historyTrip) }}</span>
              </CCardHeader>
              <CCardBody class="p-0">
                <div class="table-responsive">
                  <table class="table table-sm table-bordered text-center align-middle mb-0" style="background: rgba(255,255,255,0.05);">
                    <thead class="bg-dark text-white">
                      <tr>
                        <th class="text-white">Comprobante</th>
                        <th class="text-white">Cliente</th>
                        <th class="text-white">Documento</th>
                        <th class="text-white">Total Venta</th>
                        <th class="text-white">Pagado</th>
                        <th class="text-white" style="background: rgba(255, 193, 7, 0.2);">FALTANTE (CxC)</th>
                        <th class="text-white">Fecha Vencimiento</th>
                        <th class="text-white">Estado</th>
                        <th class="text-white">Días</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="sale in historyTrip.sales" :key="'cxc-'+sale.id" v-if="sale.credit_pending_amount > 0" style="font-size: 13px;">
                        <td class="font-weight-bold text-danger">{{ sale.invoice_code }}</td>
                        <td class="text-left text-white">{{ sale.client_name }}</td>
                        <td class="text-white">{{ sale.client_document }}</td>
                        <td class="text-white">S/ {{ parseFloat(sale.total_amount).toFixed(2) }}</td>
                        <td class="text-success">S/ {{ parseFloat(sale.credit_paid_amount || 0).toFixed(2) }}</td>
                        <td class="text-warning font-weight-bold" style="background: rgba(255, 193, 7, 0.15);">
                          S/ {{ parseFloat(sale.credit_pending_amount).toFixed(2) }}
                        </td>
                        <td class="text-white">{{ formatShortDate(sale.credit_due_date) }}</td>
                        <td>
                          <span class="badge" :class="getCxcStatusBadge(sale.credit_status)">
                            {{ sale.credit_status || 'PENDIENTE' }}
                          </span>
                        </td>
                        <td :class="getCxcDaysClass(sale)">
                          {{ getCxcDaysRemaining(sale.credit_due_date) }}
                        </td>
                      </tr>
                      <tr v-if="!hasCxcPending(historyTrip)">
                        <td colspan="9" class="text-muted py-3 font-weight-bold text-white">
                          <i class="fas fa-check-circle text-success"></i> No hay cuentas por cobrar pendientes en este viaje.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <!-- VENTAS REALIZADAS -->
        <CRow>
          <CCol md="12" class="mb-4">
            <h6 class="text-primary font-weight-bold"><i class="fas fa-shopping-cart"></i> Ventas Realizadas en Ruta</h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered table-striped text-center align-middle mb-0">
                <thead class="bg-dark text-white">
                  <tr>
                    <th>Nota Venta</th>
                    <th>Fecha</th>
                    <th>Doc. Cliente</th>
                    <th class="text-left">Cliente</th>
                    <th>Lugar / Pto Exacto</th>
                    <th>Tipo Madera</th>
                    <th>Pies</th>
                    <th>Precio</th>
                    <th>Total</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="sale in historyTrip.sales" :key="sale.id">
                    <td class="font-weight-bold text-danger">{{ sale.invoice_code || 'NV-OLD' }}</td>
                    <td><small>{{ formatShortDate(sale.operation_date) }}</small></td>
                    <td><small>{{ sale.client_document }}</small></td>
                    <td class="text-left"><small class="font-weight-bold">{{ sale.client_name }}</small></td>
                    <td><small>{{ sale.location }} <span v-if="sale.exact_point"><br>({{ sale.exact_point }})</span></small></td>
                    <td>
                      <div v-for="detail in sale.details" :key="detail.id" class="small">
                        {{ detail.wood_type_name }}
                      </div>
                    </td>
                    <td>
                      <div v-for="detail in sale.details" :key="detail.id" class="small">
                        {{ detail.pies_qty }}
                      </div>
                    </td>
                    <td>
                      <div v-for="detail in sale.details" :key="detail.id" class="small">
                        S/{{ detail.unit_price }}
                      </div>
                    </td>
                    <td class="text-success font-weight-bold">S/ {{ parseFloat(sale.total_amount).toFixed(2) }}</td>
                    <td>
                      <button class="btn btn-sm btn-info mr-1" title="Imprimir NV" @click="printNV(sale.id)"><i class="fas fa-print"></i></button>
                      <button class="btn btn-sm btn-success" title="Enviar WhatsApp" @click="sendWhatsApp(sale)"><i class="fab fa-whatsapp"></i></button>
                    </td>
                  </tr>
                  <tr v-if="historyTrip.sales.length == 0">
                    <td colspan="10" class="text-muted py-3 font-weight-bold">No se registraron ventas en este viaje.</td>
                  </tr>
                </tbody>
                <tfoot class="bg-light font-weight-bold">
                  <tr>
                    <td colspan="9" class="text-right text-dark">TOTAL VENTAS:</td>
                    <td class="text-success">S/ {{ getTotalVentasHistorial() }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CCol>
        </CRow>

        <!-- GASTOS OPERATIVOS -->
        <CRow>
          <CCol md="12" lg="6" class="mb-4" v-if="getVendedorExpenses(historyTrip) && getVendedorExpenses(historyTrip).length > 0">
            <h6 class="text-danger font-weight-bold"><i class="fas fa-receipt"></i> Gastos Operativos (Vendedor)</h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered table-striped text-center align-middle">
                <thead class="bg-dark text-white">
                  <tr>
                    <th>Fecha</th>
                    <th>Descripción</th>
                    <th>Monto</th>
                    <th>Tipo Pago</th>
                    <th>N° Op.</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="exp in getVendedorExpenses(historyTrip)" :key="exp.id">
                    <td><small>{{ formatShortDate(exp.operation_date) }}</small></td>
                    <td class="text-left"><small>{{ exp.description }}</small></td>
                    <td class="text-danger font-weight-bold">S/ {{ parseFloat(exp.amount).toFixed(2) }}</td>
                    <td><span class="badge" :class="getPaymentBadgeClass(exp.payment_method)">{{ exp.payment_method }}</span></td>
                    <td>{{ exp.operation_number || '-' }}</td>
                  </tr>
                </tbody>
                <tfoot class="bg-light font-weight-bold">
                  <tr>
                    <td colspan="2" class="text-right text-dark">TOTAL GASTOS VENDEDOR:</td>
                    <td class="text-danger">S/ {{ getTotalVendedorGastos(historyTrip) }}</td>
                    <td colspan="2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CCol>

          <CCol md="12" lg="6" class="mb-4" v-if="getChoferExpenses(historyTrip) && getChoferExpenses(historyTrip).length > 0">
            <h6 class="text-danger font-weight-bold"><i class="fas fa-receipt"></i> Gastos Operativos (Chofer)</h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered table-striped text-center align-middle">
                <thead class="bg-dark text-white">
                  <tr>
                    <th>Fecha</th>
                    <th>Descripción</th>
                    <th>Monto</th>
                    <th>Tipo Pago</th>
                    <th>N° Op.</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="exp in getChoferExpenses(historyTrip)" :key="exp.id">
                    <td><small>{{ formatShortDate(exp.operation_date) }}</small></td>
                    <td class="text-left"><small>{{ exp.description }}</small></td>
                    <td class="text-danger font-weight-bold">S/ {{ parseFloat(exp.amount).toFixed(2) }}</td>
                    <td><span class="badge" :class="getPaymentBadgeClass(exp.payment_method)">{{ exp.payment_method }}</span></td>
                    <td>{{ exp.operation_number || '-' }}</td>
                  </tr>
                </tbody>
                <tfoot class="bg-light font-weight-bold">
                  <tr>
                    <td colspan="2" class="text-right text-dark">TOTAL GASTOS CHOFER:</td>
                    <td class="text-danger">S/ {{ getTotalChoferGastos(historyTrip) }}</td>
                    <td colspan="2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CCol>
        </CRow>

        <!-- COMPRAS DE MERMA Y FLETES -->
        <CRow>
          <CCol md="12" lg="6" class="mb-4" v-if="historyTrip.purchases && historyTrip.purchases.length > 0">
            <h6 class="text-warning font-weight-bold"><i class="fas fa-seedling"></i> Compras de Aserrín / Viruta</h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered table-striped text-center align-middle">
                <thead class="bg-dark text-white">
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Cliente</th>
                    <th>Sacos</th>
                    <th>Total</th>
                    <th>Tipo Pago</th>
                    <th>N° Op.</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="pur in historyTrip.purchases" :key="pur.id">
                    <td><small>{{ formatShortDate(pur.operation_date) }}</small></td>
                    <td>{{ pur.type }}</td>
                    <td><small>{{ pur.client_name || '-' }}</small></td>
                    <td>{{ pur.sacos_qty }}</td>
                    <td class="text-danger font-weight-bold">S/ {{ parseFloat(pur.amount).toFixed(2) }}</td>
                    <td><span class="badge" :class="getPaymentBadgeClass(pur.payment_method)">{{ pur.payment_method }}</span></td>
                    <td>{{ pur.operation_number || '-' }}</td>
                  </tr>
                </tbody>
                <tfoot class="bg-light font-weight-bold">
                  <tr>
                    <td colspan="4" class="text-right text-dark">TOTAL COMPRAS:</td>
                    <td class="text-danger">S/ {{ getTotalComprasHistorial(historyTrip) }}</td>
                    <td colspan="2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CCol>

          <CCol md="12" lg="6" class="mb-4" v-if="historyTrip.return_freights && historyTrip.return_freights.length > 0">
            <h6 class="text-info font-weight-bold"><i class="fas fa-box"></i> Fletes de Retorno</h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered table-striped text-center align-middle">
                <thead class="bg-dark text-white">
                  <tr>
                    <th>Fecha</th>
                    <th>Descripción (Carga)</th>
                    <th>Ingreso Cobrado</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="freight in historyTrip.return_freights" :key="freight.id">
                    <td><small>{{ formatShortDate(freight.operation_date) }}</small></td>
                    <td class="text-left"><small>{{ freight.description }}</small></td>
                    <td class="text-success font-weight-bold">S/ {{ parseFloat(freight.amount).toFixed(2) }}</td>
                  </tr>
                </tbody>
                <tfoot class="bg-light font-weight-bold">
                  <tr>
                    <td colspan="2" class="text-right text-dark">TOTAL FLETES RETORNO:</td>
                    <td class="text-success">S/ {{ getTotalFletesRetorno(historyTrip) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CCol>
        </CRow>

        <!-- DETALLE DE DESCARGA A PREVCA -->
        <CRow>
          <CCol md="12" class="mb-4">
            <h6 class="text-primary font-weight-bold"><i class="fas fa-warehouse"></i> Detalle de Descarga a PREVCA</h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered table-striped text-center align-middle">
                <thead class="bg-dark text-white">
                  <tr>
                    <th>Tipo de Madera</th>
                    <th>Pies Descargados</th>
                    <th>Trozas Descargadas</th>
                    <th>Sacos Aserrín</th>
                    <th>Sacos Viruta</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="historyTrip.stock_details && historyTrip.stock_details.length > 0">
                    <td colspan="5" class="bg-secondary text-white font-weight-bold text-primary">MADERA</td>
                  </tr>
                  <tr v-for="detail in historyTrip.stock_details" :key="detail.id">
                    <td class="text-left font-weight-bold">{{ getWoodTypeName(detail) }}</td>
                    <td class="text-left font-weight-bold text-primary">{{ parseFloat(detail.current_pies || 0).toFixed(2) }} Pies</td>
                    <td class="text-left font-weight-bold text-primary">{{ parseFloat(detail.current_trozas || 0).toFixed(2) }} Trozas</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                  <tr v-if="historyTrip.stock_details && historyTrip.stock_details.length > 0" class="bg-light font-weight-bold">
                    <td class="text-left font-weight-bold text-primary">TOTAL MADERA:</td>
                    <td class="text-left font-weight-bold text-primary">{{ totalStockPiesHistorial }} Pies</td>
                    <td class="text-left font-weight-bold text-primary">{{ totalStockTrozasHistorial }} Trozas</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                  <tr v-if="historyTrip.stock && historyTrip.stock.aserrin_sacos > 0">
                    <td colspan="5" class="bg-secondary text-white font-weight-bold">MERMA</td>
                  </tr>
                  <tr v-if="historyTrip.stock && historyTrip.stock.aserrin_sacos > 0">
                    <td class="text-left font-weight-bold">Aserrín</td>
                    <td>-</td>
                    <td>-</td>
                    <td class="text-warning font-weight-bold">{{ historyTrip.stock.aserrin_sacos }} sacos</td>
                    <td>-</td>
                  </tr>
                  <tr v-if="historyTrip.stock && historyTrip.stock.viruta_sacos > 0">
                    <td class="text-left font-weight-bold">Viruta</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td class="text-warning font-weight-bold">{{ historyTrip.stock.viruta_sacos }} sacos</td>
                  </tr>
                  <tr v-if="(!historyTrip.stock_details || historyTrip.stock_details.length === 0) && (!historyTrip.stock || (historyTrip.stock.aserrin_sacos === 0 && historyTrip.stock.viruta_sacos === 0))">
                    <td colspan="5" class="text-muted py-3 font-weight-bold">No hay datos de descarga disponibles.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CCol>
        </CRow>

        <!-- RESUMEN DE LIQUIDACIÓN Y COSTOS -->
        <div class="bg-dark text-white p-3 rounded mt-2 shadow-sm border border-secondary">
          <h6 class="text-warning font-weight-bold mb-3 border-bottom pb-2">Resumen de Liquidación y Costos</h6>

          <div v-if="!historyTrip || !historyTrip._summary" class="text-center py-3">
            <i class="fas fa-spinner fa-spin"></i> Calculando resumen...
          </div>

          <div v-else>
            <CRow>
              <CCol xs="12" class="mb-3">
                <div class="small text-muted mb-2">DATOS BASE DEL VIAJE</div>
                <CRow class="text-center">
                  <CCol sm="3">
                    <span class="text-muted d-block">Pies Iniciales</span>
                    <strong class="text-info">{{ getSummaryValue('initialPies') }}</strong>
                  </CCol>
                  <CCol sm="3">
                    <span class="text-muted d-block">Precio Base Promedio</span>
                    <strong class="text-light">S/ {{ historyTrip._summary.precioBasePromedio ? historyTrip._summary.precioBasePromedio.toFixed(4) : getSummaryValue('supplierBasePrice') }}</strong>
                  </CCol>
                  <CCol sm="3">
                    <span class="text-muted d-block">Flete Total</span>
                    <strong class="text-light">S/ {{ getSummaryValue('freightCost') }}</strong>
                  </CCol>
                  <CCol sm="3">
                    <span class="text-muted d-block">Gastos Chofer</span>
                    <strong class="text-light">S/ {{ getSummaryValue('totalGastosChofer') }}</strong>
                  </CCol>
                </CRow>
              </CCol>

              <CCol xs="12" class="mb-3">
                <div class="small text-muted mb-2">CÁLCULOS DE COSTOS</div>
                <CRow class="text-center">
                  <CCol sm="4">
                    <span class="text-muted d-block">Costo Total de Traslado</span>
                    <strong class="text-warning">S/ {{ getSummaryValue('costoTotalTraslado') }}</strong>
                  </CCol>
                  <CCol sm="4">
                    <span class="text-muted d-block">Costo Unitario de Traslado</span>
                    <strong class="text-warning">S/ {{ getSummaryValue('costoUnitarioTraslado') }}</strong>
                  </CCol>
                  <CCol sm="4">
                    <span class="text-muted d-block">Costo Total por Pie (Promedio)</span>
                    <strong class="text-danger font-weight-bold">S/ {{ getSummaryValue('costoTotalPorPie') }}</strong>
                  </CCol>
                </CRow>
              </CCol>

              <CCol xs="12">
                <div class="small text-muted mb-2">UTILIDAD GENERADA</div>
                <CRow class="text-center">
                  <CCol sm="4">
                    <span class="text-muted d-block">Pies Vendidos</span>
                    <strong class="text-info">{{ getSummaryValue('totalPiesVendidos') }}</strong>
                  </CCol>
                  <CCol sm="4">
                    <span class="text-muted d-block">Utilidad por Pie (Promedio)</span>
                    <strong class="text-success">S/ {{ getSummaryValue('utilidadPorPie') }}</strong>
                  </CCol>
                  <CCol sm="4">
                    <span class="text-muted d-block">Utilidad Total</span>
                    <strong class="text-success font-weight-bold" style="font-size: 1.2rem;">
                      S/ {{ getSummaryValue('utilidadTotal') }}
                    </strong>
                  </CCol>
                </CRow>
              </CCol>
            </CRow>
          </div>

          <!-- DETALLE POR TIPO DE MADERA -->
          <div class="mt-3 border-top pt-2" v-if="historyTrip._summary && historyTrip._summary.woodTypeUtilidades && historyTrip._summary.woodTypeUtilidades.length > 0">
            <small class="text-muted d-block mb-2 font-weight-bold">📊 DETALLE POR TIPO DE MADERA</small>
            <div class="table-responsive">
              <table class="table table-sm table-bordered text-center align-middle" style="background: rgba(255,255,255,0.05);">
                <thead>
                  <tr class="text-muted" style="font-size: 11px;">
                    <th>Tipo de Madera</th>
                    <th>Pies Iniciales</th>
                    <th>Pies para almacen prevca</th>
                    <th>Costo sin IGV</th>
                    <th>Costo Traslado (S/)</th>
                    <th>Costo Total x Pie (S/)</th>
                    <th>Pies Vendidos</th>
                    <th>Precio Venta Prom.</th>
                    <th>Utilidad x Pie (S/)</th>
                    <th>Utilidad Total (S/)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="wt in historyTrip._summary.woodTypeUtilidades" :key="wt.wood_type_id" style="font-size: 12px;" class="text-white">
                    <td class="font-weight-bold text-white">{{ wt.wood_type_name }}</td>
                    <td>{{ wt.initial_pies }}</td>
                    <td>{{ wt.current_pies }}</td>
                    <td class="text-info font-weight-bold">S/ {{ wt.supplier_base_price.toFixed(4) }}</td>
                    <td class="text-warning">S/ {{ wt.costo_unitario_traslado.toFixed(4) }}</td>
                    <td class="text-danger font-weight-bold">S/ {{ wt.costo_total_por_pie.toFixed(4) }}</td>
                    <td>{{ wt.pies_vendidos }}</td>
                    <td>S/ {{ wt.precio_venta_promedio ? wt.precio_venta_promedio.toFixed(2) : '0.00' }}</td>
                    <td class="text-success">S/ {{ wt.utilidad_por_pie.toFixed(4) }}</td>
                    <td class="text-success font-weight-bold">S/ {{ wt.utilidad_total.toFixed(2) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr style="font-size: 12px; background: rgba(255,255,255,0.1);">
                    <td colspan="8" class="text-right font-weight-bold text-white">TOTAL UTILIDAD:</td>
                    <td colspan="2" class="text-success font-weight-bold" style="font-size: 1.1rem;">
                      S/ {{ getSummaryValue('utilidadTotal') }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <button class="btn btn-secondary font-weight-bold" @click="showHistoryModal = false">Cerrar Historial</button>
      </template>
    </CModal>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { CChartBar, CChartPie } from "@coreui/vue-chartjs";
import moment from "moment";
import Swal from "sweetalert2";

export default {
  name: "LogisticsDashboard",
  components: {
    CChartBar,
    CChartPie
  },
  data() {
    return {
      isLoading: false,
      loadingSales: false,
      loadingPurchases: false,
      loadingRanking: false,
      loadingTopProducts: false,
      globalError: null,

      filters: {
        date_from: moment().subtract(6, 'months').format("YYYY-MM-DD"),
        date_to: moment().format("YYYY-MM-DD")
      },

      totals: {
        flete_reparto: 0,
        gastos_vendedor: 0,
        gastos_chofer: 0,
        utilidad_neta: 0,
        ventas: 0,
        combustible: 0
      },

      monthlyData: [],
      monthlyLabels: [],
      monthlySalesData: [],
      monthlySalesLabels: [],
      monthlyPurchasesData: [],
      monthlyPurchasesLabels: [],
      rankingClients: [],
      topProducts: [],

      tripsBreakdown: [],
      tripsTotales: {
        ventas: 0,
        gastos: 0,
        combustible: 0,
        utilidad: 0
      },

      showHistoryModal: false,
      historyTrip: null,
      isLoadingHistory: false,

      barChartOptions: {
        maintainAspectRatio: false,
        responsive: true,
        tooltips: {
          callbacks: {
            label: function(t) {
              return 'S/ ' + Number(t.yLabel || 0).toFixed(2);
            }
          }
        },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true } }]
        }
      },

      monthlyChartOptions: {
        maintainAspectRatio: false,
        responsive: true,
        tooltips: {
          callbacks: {
            label: function(t) {
              return 'S/ ' + Number(t.yLabel || 0).toFixed(2);
            }
          }
        },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true } }]
        }
      },

      topProductsChartOptions: {
        maintainAspectRatio: false,
        responsive: true,
        tooltips: {
          callbacks: {
            label: function(t) {
              return Number(t.yLabel || 0) + ' Pies vendidos';
            }
          }
        },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true } }]
        }
      }
    };
  },
  computed: {
    barChartData() {
      return [
        { label: 'Ingresos (Utilidad)', backgroundColor: '#2eb85c', data: this.monthlyData.map(m => parseFloat(m.income) || 0) },
        { label: 'Gastos Vendedor', backgroundColor: '#e55353', data: this.monthlyData.map(m => parseFloat(m.expense) || 0) },
        { label: 'Gastos Chofer', backgroundColor: '#f9b115', data: this.monthlyData.map(m => parseFloat(m.gastos_chofer) || 0) },
        { label: 'Utilidad Neta', backgroundColor: '#321fdb', data: this.monthlyData.map(m => parseFloat(m.profit) || 0) }
      ];
    },
    pieChartData() {
      return [{
        backgroundColor: ['#2eb85c', '#e55353', '#f9b115'],
        data: [
          parseFloat(this.totals.ventas) || 0,
          parseFloat(this.totals.gastos_vendedor) || 0,
          parseFloat(this.totals.gastos_chofer) || 0
        ]
      }];
    },
    monthlySalesChartData() {
      return [{ label: 'Ventas Mensuales (S/)', backgroundColor: '#2eb85c', data: this.monthlySalesData }];
    },
    monthlyPurchasesChartData() {
      return [{ label: 'Compras de Merma (S/)', backgroundColor: '#e55353', data: this.monthlyPurchasesData }];
    },
    topProductsChartData() {
      return [{ label: 'Pies Vendidos', backgroundColor: '#3399ff', data: this.topProducts.map(p => parseFloat(p.total_pies) || 0) }];
    },
    topProductsLabels() {
      return this.topProducts.map(p => p.wood_type_name || 'Sin nombre');
    },

    totalStockPiesHistorial() {
      if (!this.historyTrip || !this.historyTrip.stock_details) return 0;
      return this.historyTrip.stock_details.reduce((sum, d) => sum + (parseFloat(d.current_pies) || 0), 0);
    },
    totalStockTrozasHistorial() {
      if (!this.historyTrip || !this.historyTrip.stock_details) return 0;
      return this.historyTrip.stock_details.reduce((sum, d) => sum + (parseFloat(d.current_trozas) || 0), 0);
    }
  },
  created() {
    this.applyAllFilters();
  },
  methods: {
    ...mapActions(["post", "get"]),

    safeNumber(value) {
      const n = parseFloat(value);
      return isNaN(n) ? '0.00' : n.toFixed(2);
    },

    formatShortDate(date) {
      if (!date) return '-';
      return moment(date).format("DD/MM/YYYY");
    },

    buildPayload(extra = {}) {
      return {
        date_from: this.filters.date_from,
        date_to: this.filters.date_to,
        ...extra
      };
    },

    async safePost(url, extraPayload = {}) {
      const payload = this.buildPayload(extraPayload);
      const token = this.$store.getters.get__token;

      try {
        const res = await this.post({ url, token, data: payload });
        return res;
      } catch (err1) {
        try {
          const res = await this.post({ url, token, params: payload });
          return res;
        } catch (err2) {
          throw err2;
        }
      }
    },

    forceRefresh() {
      this.globalError = null;
      this.applyAllFilters();
    },

    applyAllFilters() {
      this.globalError = null;
      this.loadDashboard();
      this.fetchMonthlySales();
      this.fetchMonthlyPurchases();
      this.fetchRankingData();
      this.fetchTopProducts();
    },

    // ==========================================
    // DASHBOARD PRINCIPAL
    // ==========================================
    loadDashboard() {
      this.isLoading = true;

      this.safePost(`${this.$store.getters.get__url}/logistics/dashboard-v2`)
        .then(res => {
          if (res && res.success) {
            this.totals = {
              flete_reparto: res.data?.totals?.flete_reparto || 0,
              gastos_vendedor: res.data?.totals?.gastos_vendedor || 0,
              gastos_chofer: res.data?.totals?.gastos_chofer || 0,
              utilidad_neta: res.data?.totals?.utilidad_neta || 0,
              ventas: res.data?.totals?.ventas || 0,
              combustible: res.data?.totals?.combustible || 0
            };
            this.monthlyData = res.data?.monthly || [];
            this.monthlyLabels = this.monthlyData.map(m => m.month_name || '');

            if (res.data?.trips_breakdown) {
              this.tripsBreakdown = res.data.trips_breakdown.trips || [];
              this.tripsTotales = res.data.trips_breakdown.totales || {
                ventas: 0, gastos: 0, combustible: 0, utilidad: 0
              };
            } else {
              this.tripsBreakdown = [];
              this.tripsTotales = { ventas: 0, gastos: 0, combustible: 0, utilidad: 0 };
            }
          } else {
            this.globalError = res?.message || 'No se pudo cargar el dashboard principal.';
          }
        })
        .catch(err => {
          this.globalError = err?.response?.data?.message || err?.message || 'Error cargando dashboard.';
          console.error('❌ Error dashboard:', err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    // ==========================================
    // VENTAS MENSUALES
    // ==========================================
    fetchMonthlySales() {
      this.loadingSales = true;
      this.safePost(`${this.$store.getters.get__url}/logistics/monthly-sales`)
        .then(res => {
          if (res && res.success) {
            this.monthlySalesData = (res.data || []).map(item => parseFloat(item.total) || 0);
            this.monthlySalesLabels = (res.data || []).map(item => item.month_name || '');
          }
        })
        .finally(() => { this.loadingSales = false; });
    },

    // ==========================================
    // COMPRAS MENSUALES
    // ==========================================
    fetchMonthlyPurchases() {
      this.loadingPurchases = true;
      this.safePost(`${this.$store.getters.get__url}/logistics/monthly-purchases`)
        .then(res => {
          if (res && res.success) {
            this.monthlyPurchasesData = (res.data || []).map(item => parseFloat(item.total) || 0);
            this.monthlyPurchasesLabels = (res.data || []).map(item => item.month_name || '');
          }
        })
        .finally(() => { this.loadingPurchases = false; });
    },

    // ==========================================
    // RANKING DE CLIENTES
    // ==========================================
    fetchRankingData() {
      this.loadingRanking = true;
      this.safePost(`${this.$store.getters.get__url}/logistics/client-ranking`)
        .then(res => {
          if (res && res.success) {
            this.rankingClients = (res.data || []).slice(0, 10);
          }
        })
        .finally(() => { this.loadingRanking = false; });
    },

    // ==========================================
    // TOP PRODUCTOS
    // ==========================================
    fetchTopProducts() {
      this.loadingTopProducts = true;
      this.safePost(`${this.$store.getters.get__url}/logistics/top-products`)
        .then(res => {
          if (res && res.success) {
            this.topProducts = res.data || [];
          }
        })
        .finally(() => { this.loadingTopProducts = false; });
    },

    // ==========================================
    // MODAL DE AUDITORÍA
    // ==========================================
    viewTripHistory(trip_id) {
      this.isLoadingHistory = true;
      this.showHistoryModal = true;
      this.historyTrip = null;

      this.get({
        url: `${this.$store.getters.get__url}/logistics/trips/${trip_id}`,
        token: this.$store.getters.get__token,
      }).then((res) => {
        if (res.success) {
          this.historyTrip = res.data;
          this.calculateTripSummary();
        } else {
          Swal.fire("Error", "No se pudo cargar el historial", "error");
          this.showHistoryModal = false;
        }
      }).catch((err) => {
        console.error('Error cargando historial:', err);
        Swal.fire("Error", "Ocurrió un error al cargar el historial", "error");
        this.showHistoryModal = false;
      }).finally(() => {
        this.isLoadingHistory = false;
      });
    },

    calculateTripSummary() {
      if (!this.historyTrip) return;

      const trip = this.historyTrip;
      const initialPies = parseFloat(trip.stock?.initial_pies) || 0;
      const freightCost = parseFloat(trip.freight_cost) || 0;

      const choferWallet = trip.wallets?.find(w => w.role_owner === 'CHOFER');
      let totalGastosChofer = 0;
      if (choferWallet) {
        totalGastosChofer = trip.expenses
          ?.filter(e => e.trip_wallet_id === choferWallet.id)
          ?.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0) || 0;
      }

      const costoTotalTraslado = freightCost + totalGastosChofer;
      const costoUnitarioTraslado = initialPies > 0 ? (costoTotalTraslado / initialPies) : 0;

      let utilidadTotal = 0;
      let totalPiesVendidos = 0;
      let totalPrecioBasePonderado = 0;
      let totalPiesIniciales = 0;
      const woodTypeUtilidades = [];

      if (trip.stock_details && trip.stock_details.length > 0) {
        for (const stockDetail of trip.stock_details) {
          const woodType = stockDetail.wood_type;
          const woodTypeName = woodType?.name || stockDetail.wood_type_name || 'Madera';
          const woodTypeId = stockDetail.wood_type_id;

          const supplierBasePrice = parseFloat(stockDetail.price_per_pie) || 0;
          const costoTotalPorPie = supplierBasePrice + costoUnitarioTraslado;

          let utilidadPorTipo = 0;
          let piesVendidosPorTipo = 0;
          let precioVentaPromedio = 0;
          let totalPrecioVenta = 0;

          if (trip.sales && trip.sales.length > 0) {
            for (const sale of trip.sales) {
              if (sale.details && sale.details.length > 0) {
                for (const detail of sale.details) {
                  if (detail.wood_type_id === woodTypeId) {
                    const piesVendidos = parseFloat(detail.pies_qty) || 0;
                    const precioVenta = parseFloat(detail.unit_price) || 0;
                    const utilidadPorPie = precioVenta - costoTotalPorPie;
                    utilidadPorTipo += utilidadPorPie * piesVendidos;
                    piesVendidosPorTipo += piesVendidos;
                    totalPrecioVenta += precioVenta * piesVendidos;
                  }
                }
              }
            }
          }

          precioVentaPromedio = piesVendidosPorTipo > 0 ? (totalPrecioVenta / piesVendidosPorTipo) : 0;

          woodTypeUtilidades.push({
            wood_type_id: woodTypeId,
            wood_type_name: woodTypeName,
            initial_pies: parseFloat(stockDetail.initial_pies) || 0,
            current_pies: parseFloat(stockDetail.current_pies) || 0,
            supplier_base_price: supplierBasePrice,
            costo_unitario_traslado: costoUnitarioTraslado,
            costo_total_por_pie: costoTotalPorPie,
            precio_venta_promedio: precioVentaPromedio,
            pies_vendidos: piesVendidosPorTipo,
            utilidad_total: utilidadPorTipo,
            utilidad_por_pie: piesVendidosPorTipo > 0 ? (utilidadPorTipo / piesVendidosPorTipo) : 0
          });

          utilidadTotal += utilidadPorTipo;
          totalPiesVendidos += piesVendidosPorTipo;
          totalPrecioBasePonderado += supplierBasePrice * (parseFloat(stockDetail.initial_pies) || 0);
          totalPiesIniciales += parseFloat(stockDetail.initial_pies) || 0;
        }
      }

      const precioBasePromedio = totalPiesIniciales > 0 ? (totalPrecioBasePonderado / totalPiesIniciales) : 0;

      this.historyTrip._summary = {
        initialPies: initialPies,
        freightCost: freightCost,
        totalGastosChofer: totalGastosChofer,
        costoTotalTraslado: costoTotalTraslado,
        costoUnitarioTraslado: costoUnitarioTraslado,
        precioBasePromedio: precioBasePromedio,
        utilidadTotal: utilidadTotal,
        totalPiesVendidos: totalPiesVendidos,
        woodTypeUtilidades: woodTypeUtilidades,
        woodTypeCount: woodTypeUtilidades.length
      };
    },

    getSummaryValue(key) {
      if (!this.historyTrip || !this.historyTrip._summary) return '0.00';
      const summary = this.historyTrip._summary;

      if (summary[key] !== undefined) {
        const value = summary[key];
        const numValue = parseFloat(value) || 0;
        if (key === 'costoUnitarioTraslado' || key === 'costoTotalPorPie' || key === 'utilidadPorPie' || key === 'precioBasePromedio') {
          return numValue.toFixed(4);
        }
        return numValue.toFixed(2);
      }

      if (key === 'supplierBasePrice') return (summary.precioBasePromedio || 0).toFixed(4);
      return '0.00';
    },

    // Helpers del modal
    getChoferWallet(trip) {
      if (!trip || !trip.wallets) return null;
      return trip.wallets.find(w => w.role_owner == 'CHOFER');
    },
    getVendedorWallet(trip) {
      if (!trip || !trip.wallets) return null;
      return trip.wallets.find(w => w.role_owner == 'VENDEDOR');
    },
    getChoferExpenses(trip) {
      if (!trip || !trip.expenses || !trip.wallets) return [];
      const w = this.getChoferWallet(trip);
      if (!w) return [];
      return trip.expenses.filter(e => e.trip_wallet_id == w.id);
    },
    getVendedorExpenses(trip) {
      if (!trip || !trip.expenses || !trip.wallets) return [];
      const w = this.getVendedorWallet(trip);
      if (!w) return [];
      return trip.expenses.filter(e => e.trip_wallet_id == w.id);
    },
    getTotalChoferIngresos(trip) {
      let total = 0;
      const w = this.getChoferWallet(trip);
      total += parseFloat(trip.initial_cash || 0);
      if (w && w.transactions) {
        total += w.transactions.filter(t => t.type === 'RECARGA').reduce((s, t) => s + parseFloat(t.amount || 0), 0);
      }
      if (trip.return_freights) {
        total += trip.return_freights.reduce((s, f) => s + parseFloat(f.amount || 0), 0);
      }
      return total.toFixed(2);
    },
    getTotalChoferEgresos(trip) {
      return this.getChoferExpenses(trip).reduce((s, e) => s + parseFloat(e.amount || 0), 0).toFixed(2);
    },
    getTotalChoferNeto(trip) {
      return (parseFloat(this.getTotalChoferIngresos(trip)) - parseFloat(this.getTotalChoferEgresos(trip))).toFixed(2);
    },
    getTotalVendedorIngresos(trip) {
      let total = 0;
      const w = this.getVendedorWallet(trip);
      if (w && w.transactions) {
        total += w.transactions.filter(t => t.type === 'RECARGA').reduce((s, t) => s + parseFloat(t.amount || 0), 0);
      }
      if (trip.sales) {
        total += trip.sales.filter(s => s.status === 'ACTIVO').reduce((s, s2) => s + parseFloat(s2.total_amount || 0), 0);
      }
      return total.toFixed(2);
    },
    getTotalVendedorEgresos(trip) {
      let total = 0;
      total += this.getVendedorExpenses(trip).reduce((s, e) => s + parseFloat(e.amount || 0), 0);
      if (trip.purchases) {
        total += trip.purchases.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
      }
      return total.toFixed(2);
    },
    getTotalVendedorNeto(trip) {
      return (parseFloat(this.getTotalVendedorIngresos(trip)) - parseFloat(this.getTotalVendedorEgresos(trip))).toFixed(2);
    },
    getTotalVendedorGastos(trip) {
      return this.getVendedorExpenses(trip).reduce((s, e) => s + parseFloat(e.amount || 0), 0).toFixed(2);
    },
    getTotalChoferGastos(trip) {
      return this.getChoferExpenses(trip).reduce((s, e) => s + parseFloat(e.amount || 0), 0).toFixed(2);
    },
    getTotalVentasHistorial() {
      if (!this.historyTrip || !this.historyTrip.sales) return '0.00';
      return this.historyTrip.sales.reduce((s, s2) => s + parseFloat(s2.total_amount || 0), 0).toFixed(2);
    },
    getTotalComprasHistorial(trip) {
      if (!trip || !trip.purchases) return '0.00';
      return trip.purchases.reduce((s, p) => s + parseFloat(p.amount || 0), 0).toFixed(2);
    },
    getTotalFletesRetorno(trip) {
      if (!trip || !trip.return_freights) return '0.00';
      return trip.return_freights.reduce((s, f) => s + parseFloat(f.amount || 0), 0).toFixed(2);
    },
    getPaymentBadgeClass(method) {
      const c = { EFECTIVO:'badge-success', YAPE:'badge-info', PLIN:'badge-info', TRANSFERENCIA:'badge-primary', MIXTO:'badge-warning' };
      return c[method] || 'badge-secondary';
    },
    hasCxcPending(trip) {
      if (!trip || !trip.sales) return false;
      return trip.sales.some(s => s.credit_pending_amount > 0);
    },
    getTotalCxcPendingHistorial(trip) {
      if (!trip || !trip.sales) return '0.00';
      return trip.sales.reduce((sum, s) => s.credit_pending_amount > 0 ? sum + parseFloat(s.credit_pending_amount) : sum, 0).toFixed(2);
    },
    getCxcStatusBadge(status) {
      const b = { PENDIENTE:'badge-warning', PARCIAL:'badge-info', PAGADO:'badge-success', VENCIDO:'badge-danger', ANULADO:'badge-secondary' };
      return b[status] || 'badge-secondary';
    },
    getCxcDaysRemaining(dueDate) {
      if (!dueDate) return '-';
      return moment(dueDate).startOf('day').diff(moment().startOf('day'), 'days');
    },
    getCxcDaysClass(sale) {
      if (!sale || !sale.credit_due_date) return '';
      const d = this.getCxcDaysRemaining(sale.credit_due_date);
      if (d < 0) return 'text-danger font-weight-bold';
      if (d <= 3) return 'text-warning font-weight-bold';
      return 'text-white';
    },
    getWoodTypeName(detail) {
      if (detail.wood_type && detail.wood_type.name) return detail.wood_type.name;
      if (detail.wood_type_name) return detail.wood_type_name;
      return 'Madera';
    },
    printNV(id) {
      window.open(`${this.$store.getters.get__url}/logistics/sales/ticket/${id}`, "_blank");
    },
    sendWhatsApp(sale) {
      const urlPdf = `${this.$store.getters.get__url}/logistics/sales/ticket/${sale.id}`;
      const textMessage = `Hola *${sale.client_name}*.%0A%0AAdjunto Nota Venta: *${sale.invoice_code || 'S/N'}*.%0ATotal: *S/ ${parseFloat(sale.total_amount).toFixed(2)}*.%0A%0APuedes descargar o ver tu comprobante aquí:%0A${urlPdf}`;
      window.open(`https://api.whatsapp.com/send?text=${textMessage}`, '_blank');
    }
  }
};
</script>

<style scoped>
.badge {
  font-size: 12px;
  padding: 6px 12px;
}
</style>